import { useEffect, type FormEvent } from "react";
import { useNavigate } from "react-router-dom-v5-compat";
import { BestPriceGuaranteeLink } from "@apac/components/ui";
import { useBooleanFlag } from "@apac/feature-flags";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { CoreFlightsComponent } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import { FeatureFlagNames } from "@hsbc/core-utilities";
import { DEFAULT_FLIGHT_ORIGIN } from "@hsbc/shared";
import {
  CalendarPicker,
  FlightCategorySelection,
  FlightPassengerSelection,
  useFlightSearch,
  type FlightSearchComponentProps,
} from "@tenant/flights";
import { Icon } from "@tenant/ui";
import clsx from "clsx";
import { CalendarDialogHeader } from "../../../components";
import styles from "./styles.module.scss";

export const FlightSearch = (props: FlightSearchComponentProps) => {
  const {
    className,
    children,
    showCalendarBackButton,
    onSearch: onSearchProp,
  } = props;

  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, CoreFlightsComponent.FlightSearch);

  const {
    context,
    handlers: {
      onSearch,
      onOriginChange,
      onCalendarClose,
      onNavigateToCalendar,
      onPassengerCountChange,
      onTripCategoryChange,
    },
  } = useFlightSearch();

  const { origin, destination, validSearchParams } = context;

  useEffect(() => {
    // Set the default origin if it's not set, and exists
    if (!origin && DEFAULT_FLIGHT_ORIGIN != null) {
      onOriginChange(DEFAULT_FLIGHT_ORIGIN);
    }
  }, [onOriginChange, origin]);

  const onSubmitForm = (e?: FormEvent) => {
    e?.preventDefault();
    if (onSearchProp != null) {
      onSearchProp();
    } else if (validSearchParams != null) {
      onSearch(validSearchParams);
    }
  };
  const navigate = useNavigate();

  const enableBPG = useBooleanFlag(FeatureFlagNames.BEST_PRICE_GUARANTEE);

  return (
    <div className={clsx(block, className)}>
      <form className={clsx(cn("form"))} onSubmit={onSubmitForm}>
        <div className={cn("category-selection-container")}>
          <FlightCategorySelection
            tripCategoryChangeHandler={onTripCategoryChange}
            category={context.tripCategory}
          />
        </div>

        <div
          className={clsx(
            cn("traveler-selection-container"),
            cn("cell-container")
          )}
        >
          <span className={cn("cell-title")}>
            {t("flightsPageTitles.travellers")}
          </span>
          <div className={cn("cell-content")}>
            <FlightPassengerSelection
              updatePaxCount={onPassengerCountChange}
              paxCount={context.passengerCounts}
            />
          </div>
        </div>

        <div
          className={clsx(
            cn("cell-container"),
            cn("origin-selection-container")
          )}
        >
          <span className={cn("cell-title")}>
            {t("home.originSearchTitle")}
          </span>
          <button
            className={cn("location-button", {
              filled: origin?.label != null,
            })}
            onClick={() => navigate("/search/origin")}
            aria-label={t("home.originSearchTitle")}
          >
            <div className={cn("location-button-value")}>
              {origin?.label ?? t("home.flightLocationSearchLabel")}
            </div>
            <Icon iconName={IconNameEnum.search} />
          </button>
        </div>

        <div
          className={clsx(
            cn("cell-container"),
            cn("destination-selection-container")
          )}
        >
          <span className={cn("cell-title")}>
            {t("home.destinationSearchTitle")}
          </span>
          <button
            className={cn("location-button", {
              filled: destination?.label != null,
            })}
            onClick={() => navigate("/search/destination")}
            aria-label={t("home.destinationSearchTitle")}
          >
            <div className={cn("location-button-value")}>
              {destination?.label ?? t("home.flightLocationSearchLabel")}
            </div>
            <Icon iconName={IconNameEnum.search} />
          </button>
        </div>

        {children}
      </form>
      <div className={clsx(cn("continue-button-container"))}>
        <button
          onClick={onNavigateToCalendar}
          disabled={!context.isSearchCompleteWithoutDates}
          className={cn("continue-button")}
          aria-label={t("core-flights.search.dateSelection.chooseDates")}
        >
          {t("core-flights.search.dateSelection.chooseDates")}
        </button>
      </div>

      {enableBPG === true && (
        <BestPriceGuaranteeLink iconName={IconNameEnum.cashBag} />
      )}

      <CalendarPicker
        open={context.openCalendar}
        onSearch={onSubmitForm}
        onClose={onCalendarClose}
        showBackButton={showCalendarBackButton}
        preventSameDateSelection={false}
        header={
          <CalendarDialogHeader
            tripCategory={context.tripCategory}
            origin={origin}
            destination={destination}
            passengerCounts={context.passengerCounts}
          />
        }
      />
    </div>
  );
};
